import styles from '../TargetGroupView.module.css';
import { Link, useNavigate } from 'react-router-dom';
import { AddIcon, DuplicateIcon, PeopleIcon } from 'evergreen-ui';
import { TargetGroupSettings } from '../TargetGroupSettings/TargetGroupSettings';
import AppButton from '../../../components/AppButton/AppButton';
import TargetGroupScoreDownload from '../TargetGroupScoreDownload/TargetGroupScoreDownload';
import TargetGroupFeedbackDownload from '../TargetGroupFeedbackDownload/TargetGroupFeedbackDownload';
import React, { useState } from 'react';
import { APP_ENVIRONMENT } from '../../../utils/environment-variables';
import { ReactComponent as WhiteCheckMarkIcon } from '../../../assets/Icons/white-check-mark.svg';

type GroupHeaderProps = {
    isSuperAdmin: boolean;
    hasMultipleCompanies: boolean;
    groupName: string;
    companyName: string;
    companySlug: string;
    participantsCount: number;
    groupType: 'CANDIDATE' | 'INTERNAL';
    groupId: string;
    companyId: string;
    isTargetGroupPublic: boolean;
    showAddParticipants: boolean;
    showDownloadScores: boolean;
    showDownloadFeedback: boolean;
} & (
    | { showSettings: true; scanTemplateId: string }
    | { showSettings: false; scanTemplateId?: string }
) &
    (
        | {
              showDownloadScores: true;
              scanTemplateName: string;
              scanTemplateLanguage: string;
          }
        | {
              showDownloadScores: false;
              scanTemplateName?: string;
              scanTemplateLanguage?: string;
          }
    ) &
    (
        | { showDownloadFeedback: true; scanTemplateName: string }
        | { showDownloadFeedback: false; scanTemplateName?: string }
    );

const GroupHeader = (props: GroupHeaderProps) => {
    const {
        isSuperAdmin,
        hasMultipleCompanies,
        groupName,
        companyName,
        companySlug,
        participantsCount,
        groupType,
        scanTemplateId,
        scanTemplateName,
        groupId,
        companyId,
        scanTemplateLanguage,
        isTargetGroupPublic,
        showAddParticipants,
        showDownloadScores,
        showDownloadFeedback,
        showSettings,
    } = props;

    const navigate = useNavigate();

    const [isLinkCopied, setIsLinkCopied] = useState(false);

    const baseUrl =
        APP_ENVIRONMENT === 'local'
            ? 'http://localhost:3000'
            : APP_ENVIRONMENT === 'dev'
            ? 'https://dev-demo.neurolytics.ai'
            : 'https://scan.neurolytics.ai';

    const registrationUrl = isTargetGroupPublic
        ? `${baseUrl}/register/${companySlug}/${groupId}`
        : '';

    const handleCopyClick = () => {
        navigator.clipboard.writeText(registrationUrl).then(() => {
            setIsLinkCopied(true);
            setTimeout(() => {
                setIsLinkCopied(false);
            }, 2000);
        });
    };

    return (
        <div className={styles.target_group_info_box}>
            <div className={styles.target_group_info_box__wrapper}>
                <div className={styles.target_group_info_box__info}>
                    <div className={styles.target_group_info_box__name}>
                        <h3
                            className={
                                styles.target_group_info_box__company_name
                            }>
                            {hasMultipleCompanies ? (
                                <Link
                                    className={
                                        styles.target_group_info_box__company_name__color
                                    }
                                    to={`/${companySlug}`}>
                                    {companyName}
                                </Link>
                            ) : (
                                <span
                                    className={
                                        styles.target_group_info_box__company_name__color
                                    }>
                                    {companyName}
                                </span>
                            )}
                        </h3>
                        <h2>{groupName}</h2>
                    </div>
                    <div className={styles.number_of_candidates}>
                        <div className={styles.number_of_candidates__icon}>
                            <PeopleIcon size={13} />
                        </div>
                        <span className={styles.number_of_candidates__text}>
                            {participantsCount === 1 &&
                            groupType === 'CANDIDATE'
                                ? `1 candidate`
                                : participantsCount === 1 &&
                                  groupType === 'INTERNAL'
                                ? `1 participant`
                                : participantsCount !== 1 &&
                                  groupType === 'INTERNAL'
                                ? `${participantsCount} participants`
                                : `${participantsCount} candidates`}
                        </span>
                    </div>
                </div>
                {showSettings && (
                    <TargetGroupSettings
                        isSuperAdmin={isSuperAdmin}
                        assessmentId={scanTemplateId}
                        groupId={groupId}
                        companySlug={companySlug}
                        registrationUrl={registrationUrl}
                    />
                )}
                <div
                    className={styles.target_group_info_box__public_link}
                    style={{
                        border: isTargetGroupPublic
                            ? '1px solid #c1c4d6'
                            : 'none',
                    }}>
                    {isTargetGroupPublic && (
                        <>
                            <AppButton
                                icon={DuplicateIcon}
                                color={'grayWhite'}
                                fontSize="14px"
                                fontWeight="normal"
                                onClick={handleCopyClick}>
                                Copy registration link
                            </AppButton>
                            {isLinkCopied && (
                                <div
                                    className={
                                        styles.target_group_info_box__copy_tooltip
                                    }>
                                    <WhiteCheckMarkIcon />
                                    <p>URL copied</p>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
            <div className={styles.target_group_info_box__add_participant}>
                {showDownloadScores && (
                    <TargetGroupScoreDownload
                        assessmentName={scanTemplateName}
                        targetGroupName={groupName}
                        participantsCount={participantsCount}
                        companyId={companyId}
                        groupId={groupId}
                    />
                )}
                {isSuperAdmin && showDownloadFeedback && (
                    <TargetGroupFeedbackDownload
                        companyId={companyId}
                        companyName={companyName}
                        groupId={groupId}
                        assessmentName={scanTemplateName}
                        targetGroupName={groupName}
                        assessmentLanguage={scanTemplateLanguage}
                        totalParticipants={participantsCount}
                    />
                )}

                <div>
                    {showAddParticipants && (
                        <AppButton
                            justifyContent={'flex-start'}
                            width={'auto'}
                            icon={AddIcon}
                            onClick={() => navigate('add')}>
                            Add candidates
                        </AppButton>
                    )}
                </div>
            </div>
        </div>
    );
};

export default GroupHeader;
